import React from 'react';
import { Share2, Check } from 'lucide-react';

const MU_LOGO_URL =
  'https://customer-assets.emergentagent.com/job_career-heat-map/artifacts/8zniuor4_MU%20Logo.png';

export const StickyHeader = ({ step, onShare, shareCopied, isLanding }) => {
  const isResults = step === 'results';
  const steps = [
    { id: 1, label: 'Upload Resume' },
    { id: 2, label: 'Verify Details' },
    { id: 3, label: 'Career Paths' },
  ];
  const stepMap = { upload: 1, contact: 2, analyzing: 2, results: 3 };
  const currentStep = stepMap[step] || 1;
  const showShare = step === 'results' && onShare;

  /* Short label for mobile — fits cleanly in 390px pill */
  const MobileLabel = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0, overflow: 'hidden' }}>
      <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '12px', fontWeight: 700, color: '#111111', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        Career Analysis
      </span>
      <span style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0, backgroundColor: '#E8A020', color: '#fff', fontSize: '8px', fontWeight: 700, padding: '2px 6px', borderRadius: '9999px', letterSpacing: '0.07em', lineHeight: '14px' }}>
        BETA
      </span>
    </div>
  );

  /* Full label for desktop */
  const DesktopLabel = ({ small }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
      <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: small ? '11px' : '13px', fontWeight: 700, color: '#111111', whiteSpace: 'nowrap' }}>
        Career Analysis Report
      </span>
      <span style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0, backgroundColor: '#E8A020', color: '#fff', fontSize: '8px', fontWeight: 700, padding: '2px 6px', borderRadius: '9999px', letterSpacing: '0.07em', lineHeight: '14px' }}>
        BETA
      </span>
    </div>
  );

  return (
    /* position: fixed keeps it visible on scroll regardless of parent overflow rules */
    <div
      style={{ position: 'fixed', top: '10px', left: '12px', right: '12px', zIndex: 50, pointerEvents: 'none' }}
      className={isLanding ? 'header-wrapper-landing' : 'header-wrapper'}
    >
      <header
        data-testid="sticky-header"
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '9999px',
          border: '1px solid rgba(0,0,0,0.08)',
          boxShadow: '0 2px 16px rgba(0,0,0,0.10), 0 1px 3px rgba(0,0,0,0.05)',
          height: '52px',
          display: 'flex', alignItems: 'center',
          padding: '0 16px', gap: '8px',
          maxWidth: '1200px', margin: '0 auto',
          pointerEvents: 'auto',
          /* overflow:hidden clips center content — use visible so text truncation works */
          overflow: 'hidden',
        }}
      >
        {/* Left: logo — same minWidth as right to keep center truly centred */}
        <div data-testid="brand-logo" style={{ flexShrink: 0, display: 'flex', alignItems: 'center', minWidth: '90px' }}>
          <img src={MU_LOGO_URL} alt="Masters' Union" className="header-logo-img"
            style={{ width: 'auto', objectFit: 'contain', display: 'block' }} />
        </div>

        {/* Center */}
        <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'hidden', minWidth: 0 }}>
          {isResults ? (
            /* Results: always show the full label on desktop, short on mobile */
            <>
              <div className="header-step-only-desktop"><DesktopLabel /></div>
              <div className="header-mobile-label"><MobileLabel /></div>
            </>
          ) : (
            <>
              {/* Desktop: full stepper */}
              <div className="header-step-only-desktop" style={{ alignItems: 'center', gap: '4px' }}>
                {steps.map((s, i) => (
                  <React.Fragment key={s.id}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <div style={{ width: '22px', height: '22px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, fontFamily: 'DM Sans, sans-serif', flexShrink: 0, backgroundColor: currentStep >= s.id ? '#E8A020' : '#f0f0f0', color: currentStep >= s.id ? '#ffffff' : 'rgba(0,0,0,0.32)', border: currentStep >= s.id ? 'none' : '1px solid rgba(0,0,0,0.10)' }}>
                        {currentStep > s.id ? '\u2713' : s.id}
                      </div>
                      <span className="header-step-label" style={{ fontSize: '12px', fontFamily: 'DM Sans, sans-serif', color: currentStep === s.id ? '#111111' : 'rgba(0,0,0,0.32)', fontWeight: currentStep === s.id ? 600 : 400, whiteSpace: 'nowrap' }}>
                        {s.label}
                      </span>
                    </div>
                    {i < steps.length - 1 && (
                      <div className="header-step-connector" style={{ width: '22px', height: '1px', flexShrink: 0, backgroundColor: currentStep > s.id ? '#E8A020' : 'rgba(0,0,0,0.12)' }} />
                    )}
                  </React.Fragment>
                ))}
              </div>
              {/* Mobile: short "Career Analysis BETA" */}
              <div className="header-mobile-label"><MobileLabel /></div>
            </>
          )}
        </div>

        {/* Right: share button OR spacer (same minWidth as logo to balance) */}
        <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', minWidth: '90px' }}>
          {showShare && (
            <button onClick={onShare} title="Copy shareable link"
              style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 12px', backgroundColor: shareCopied ? 'rgba(63,168,106,0.10)' : '#f5f5f5', border: `1px solid ${shareCopied ? 'rgba(63,168,106,0.30)' : 'rgba(0,0,0,0.10)'}`, borderRadius: '9999px', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontSize: '12px', fontWeight: 500, color: shareCopied ? '#3FA86A' : '#111111', whiteSpace: 'nowrap', transition: 'background-color 0.2s ease' }}
              onMouseEnter={e => { if (!shareCopied) e.currentTarget.style.backgroundColor = '#eeeeee'; }}
              onMouseLeave={e => { if (!shareCopied) e.currentTarget.style.backgroundColor = '#f5f5f5'; }}
            >
              {shareCopied ? <Check size={13} strokeWidth={2.5} /> : <Share2 size={13} />}
              <span className="header-share-text">{shareCopied ? 'Copied!' : 'Share'}</span>
            </button>
          )}
        </div>
      </header>
    </div>
  );
};
